// ============================================================================
// CSV building + download — ONE implementation for the whole app.
// ----------------------------------------------------------------------------
// Lifted verbatim from ClientsPage.jsx's C7 export helpers (SPEC-analysis-page
// §3.7 / build slice S6): ClientsPage re-imports these and drops its copies, and
// the Analysis evidence drawer uses the same functions, so a spreadsheet opened
// from either surface obeys the same escaping rules. Two CSV writers drift, and
// the drift shows up as one surface producing files Excel mangles.
//
// Escape rules (RFC 4180):
//   - A field containing " , \r \n is wrapped in "..." with internal " doubled.
//   - Otherwise emitted raw.
//   - Rows join with \r\n and the file opens with a UTF-8 BOM so Excel decodes
//     accents and the $ figures correctly.
// ============================================================================

export const csvEscape = (v: unknown): string => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

/**
 * Build a CSV string from header names + rows keyed BY HEADER NAME. A row
 * missing a header's key emits an empty field, never the string "undefined".
 */
export const buildCsv = (
    headers: string[],
    rows: Record<string, string | number | null | undefined>[],
): string => {
    const lines = [headers.map(csvEscape).join(',')];
    for (const r of rows) {
        lines.push(headers.map(h => csvEscape(r[h])).join(','));
    }
    // The string below opens with the raw UTF-8 BOM character (U+FEFF,
    // invisible here) so Excel decodes the file as UTF-8 — verbatim from the
    // ClientsPage original. The test suite pins the leading byte, so any
    // editor or codemod that silently strips it turns the build red.
    return '﻿' + lines.join('\r\n');
};

/** Trigger a browser download of the CSV. No-op outside a window (tests, SSR). */
export const downloadCsv = (filename: string, csv: string): void => {
    if (typeof window === 'undefined') return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/** Date stamp for filenames: 20260729. */
export const yyyymmdd = (): string => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};
